import * as React from 'react';
import { Debounce } from 'react-throttle';
import VisibilitySensor from 'react-visibility-sensor';

import Item from '../Item';
import SpecialItem from '../SpecialItem';
import Modal from 'src/components/common/Modal';
import ItemLoader from '../ItemLoader';
import Alert from 'src/components/common/Alert';
import env from 'src/shared/env';
import ListModel from 'src/model/list.model';
import ListItemModel from 'src/model/listItem.model';
import UserModel from 'src/model/user.model';
import { ISelectOption } from 'src/components/common/Select2/components/SelectOption/SelectOption';
import { IListsError, IListsSuccess } from 'src/store/modules/lists/reducers';
import { ICartError, ICartSuccess } from 'src/store/modules/cart/reducers';
import cartServiceInstance, {
  ICartServiceItem
} from 'src/services/cart/CartService';
import { whiteSpacesValidation as hasWhiteSpaces } from 'src/utils/whiteSpacesValidation';
import clearCart from 'src/services/cart/CartService';
import { goToCart, cartUrl } from 'src/utils/navigation';
import './ItemsList.scss';
import Loader from 'src/components/common/Loader';

interface IItemsListProps {
  updating: boolean;
  list: ListModel;
  userData: UserModel;
  error: IListsError;
  success: IListsSuccess;
  cartError: ICartError;
  cartSuccess: ICartSuccess;
  warranties: ISelectOption[];
  cartIsPending: boolean;
  onClear?: () => void;

  onClearLists(id: number): void;
  handleUpdateList(id: number, name: string): void;
  handleDeleteList(id: number): void;
  handleItemUpdate(
    itemId: number,
    name: string,
    mfc: string,
    quantity?: number,
    shipmentMarking?: string,
    warrantyProvider?: string,
    claims?: string
  ): void;
  handleRemoveItem(itemId: number): void;
  handleItemDetailsUpdate(itemId: number): void;
  handleItemsDetailsUpdate(itemsIds: number[]): void;
  handleAddToCart(
    claims: string,
    delivery: string,
    deliveryLocation: string,
    oneTimeAddress: null,
    partNumber: string,
    quantity: number,
    rushOrder: boolean,
    shipmentMarking: string,
    shipsfrom: string,
    ticketNumber: string,
    warrantyProvider: string
  ): void;
  handleAddListToCart(items: ICartServiceItem[]): void;
  closeAlertMessage():void;
  closeRemoveAlertMessage():void;
  t?: any;
}

interface IItemsListState {
  editingName: boolean;
  name: string;
  showModal: boolean;
  modalText: string;
  modalFunct: () => void;
  modalButtonText: string;
  searchText: string;
  isInvalidName: boolean;
  consignmentFlags: {};
  showAlertModal: boolean;
  confirmAddToCart: boolean;
}

class ItemsList extends React.Component<IItemsListProps, IItemsListState> {
  constructor(props: any) {
    super(props);
    this.state = {
      editingName: false,
      name: '',
      showModal: false,
      modalText: '',
      modalFunct: null,
      modalButtonText: '',
      searchText: '',
      isInvalidName: false,
      consignmentFlags: {} as { [key: string]: boolean },
      showAlertModal: false,
      confirmAddToCart: true
    };
  }

  private showEditing = () => {
    this.setState({ editingName: true });
  };

  private handleChangeListName = (value: string) => {
    this.setState({ name: value, isInvalidName: false });
  };

  private handleUpdateName = () => {
    if (!hasWhiteSpaces(this.state.name)) {
      this.props.handleUpdateList(this.props.list.listNumber, this.state.name);
      this.setState({ editingName: false });
    } else {
      this.setState({
        isInvalidName: true,
        name: ''
      });
    }
  };

  private handleShowHideModal = () => {
    this.setState(prevState => ({ showModal: !prevState.showModal }));
  };

  private handleShowHideAlertModal = () => {
    this.setState(prevState => ({ showAlertModal: !prevState.showAlertModal }));
  };

  private handleConfirmAddToCart = () => {
   this.setState(
    { confirmAddToCart: false, showAlertModal: false },
    () => {
      this.handleAddListToCart();
    }
  );
  };

  

  private deleteList = () => {
    this.props.handleDeleteList(this.props.list.listNumber);
    this.setState({ showModal: false });
  };

  private deleteItem = (itemId: number) => {
    this.props.handleRemoveItem(itemId);
    this.setState({ showModal: false });
  };

  private handleClickDeleteList = () => {
    const { t } = this.props;
    this.setState({
      showModal: true,
      modalText: `${t('common.areYouSureToDelete')} "${this.props.list.name}"?`,
      modalButtonText: 'Delete',
      modalFunct: () => this.deleteList()
    });
  };

  private handleClickRemoveItem = (itemId: number, name: string) => {
    const { t } = this.props;
    this.setState({
      showModal: true,
      modalText: `${t('common.areYouSureToDelete')} "${name}?"`,
      modalButtonText: 'Delete',
      modalFunct: () => this.deleteItem(itemId)
    });
  };

  private handleSearchInputChange = (e: any) => {
    this.setState({ searchText: e.target.value });
    this.handleSearchItemChange();
  };

  private handleSearchItemChange = () => {
    let results: string[] = Object.keys(this.props.list.listItems);
    if (this.state.searchText) {
      results = Object.keys(this.props.list.listItems).filter(key => {
        // Countries under 1000000000
        const name = `${this.props.list.listItems[key].manufacturerCode} ${this.props.list.listItems[key].name}`.toLowerCase();
        return name.includes(this.state.searchText.toLowerCase());
      });
    }
    return results;
  };

  private visibilityTriggeredItems = new Set<number>();

  private updateItem = (visible: boolean, item: ListItemModel) => {
    const itemId = item.listItemNumber;

    if (!this.visibilityTriggeredItems.has(itemId)) {
      this.visibilityTriggeredItems.add(itemId);
      return;
    }

    if (visible && item.itemDetailsState === 'pending') {
      this.props.handleItemDetailsUpdate(item.listItemNumber);
    }
  };

  private handleAddListToCart = () => {
    const items: ICartServiceItem[] = [];

    const listItems = this.props.list.listItems;
    let hasConsignment = false;
    let hasOutOfStock = false;
    Object.keys(listItems).forEach(key => {
      const item = listItems[key];
      const isConsignment =
        item?.warehouseDefault?.description?.includes("Consignment warehouse");
      const isOutOfStock = item?.inStock === false;
  
      if (isConsignment) {
        hasConsignment = true;
      }
  
      if (isOutOfStock) {
        hasOutOfStock = true;
      }
    });

    const { t } = this.props;
    let modalTextFormat= '';
    modalTextFormat= `${t('myFavourites.addToCartAlertPurchase')}`;
    if(hasConsignment && hasOutOfStock  && this.state.confirmAddToCart===true){
      this.setState({
        showAlertModal: true,
        modalText: `${modalTextFormat}`,
        modalButtonText: `${t('common.clear')}`,
        modalFunct: () => this.clearall(this.props.list.listNumber)
      });
      return;
    }

    for (const key in this.props.list.listItems) {
      if (!this.props.list.listItems.hasOwnProperty(key)) {
        continue;
      }

      const entry = this.props.list.listItems[key];
      if (hasConsignment  && this.state.confirmAddToCart === false) {
        if (entry.inStock === false) {
          continue; // skip this item
        }
      }     
      // Skip WSER category items as they are not added to cart 
       if (this.props.list.listItems[key]?.category?.trim() && this.props.list.listItems[key].category.trim() === "WSER") {
        continue; // skip this item
       }
        items.push({
          claims: this.props.list.listItems[key].dispatch,
          delivery: null,
          deliveryLocation: null, // this.props.userData.address && this.props.userData.address.number.toString(),
          partNumber: `${this.props.list.listItems[key].manufacturerCode}  ${this.props.list.listItems[key].name}`,
          quantity:
            this.props.list.listItems[key].quantity !== null &&
            this.props.list.listItems[key].quantity > 0
              ? this.props.list.listItems[key].quantity
              : 1,
          rushOrder: false,
          shipmentMarking: this.props.list.listItems[key].shipmentMarking,
          shipsfrom: 'closest', // this.props.userData.warehouseInfo && this.props.userData.warehouseInfo.code,
          ticketNumber: '', // ticket number
          warrantyProvider: this.props.list.listItems[key].warrantyProvider
        });
    }
    this.props.handleAddListToCart(items);
  };

  private renderModal = () => {
    const { t } = this.props;
    
    return (
      <Modal handleCloseModal={this.handleShowHideModal}>
        <div className="rp-lists-items-list--modal">
          <span className="rpds-close" onClick={this.handleShowHideModal} />
          <div className="rp-lists-items-list--modal-text">
            {this.state.modalText}
          </div>
          <div className="rp-lists-items-list--modal-buttons">
            <button
              className="secondary-button"
              onClick={this.handleShowHideModal}
            >
              {t('common.cancel')}
            </button>
            <button
              className="primary-button"
              onClick={e => this.state.modalFunct()}
            >
              {this.state.modalButtonText}
            </button>
          </div>
        </div>
      </Modal>
    );
  };


  private renderAlertModal = () => {
    const { t } = this.props;
    return (
      <Modal handleCloseModal={this.handleShowHideAlertModal}>
        <div className="rp-lists-items-list--modal">
          <span className="rpds-close" onClick={this.handleShowHideAlertModal} />
          <div className="rp-lists-items-list--modal-text">
            {this.state.modalText}
          </div>
          <div className="rp-lists-items-list--modal-buttons">
            <button
              className="secondary-button"
              onClick={this.handleShowHideAlertModal}
            >
              {t('common.cancel')}
            </button>
            <button
              className="primary-button "
              onClick={this.handleConfirmAddToCart}
            >
              {t('common.addToCart')}
            </button> 
          </div>
        </div>
      </Modal>
    );
  };

  private addListToCartButton = () => {
    const { t } = this.props;
    return (
      this.props.list.listItems &&
      Object.keys(this.props.list.listItems).length > 0 && (
        <div className="rp-list-items-list--header-add-to-cart">
          <button
            disabled={this.props.cartIsPending}
            className="primary-button"
            onClick={e => this.handleAddListToCart()}
          >
            <span className="rpds-shopping-cart" />
            {t('myFavourites.addListToCart')}
          </button>
        </div>
      )
    );
  };
  private handleClearAll = (id: number) => {
    const { t } = this.props;
    this.setState({
      showModal: true,
      modalText: `${t('myFavourites.ClearAllParts')}?`,
      modalButtonText: `${t('common.clear')}`,
      modalFunct: () => this.clearall(this.props.list.listNumber)
    });
  };
  private clearall = (listnum: any) => {
    this.props.onClearLists(listnum);
  };
  private clearList = () => {
    const { t } = this.props;
    return (
      this.props.list.listItems &&
      Object.keys(this.props.list.listItems).length > 0 && (
        <div className="rp-list-items-list--header-add-to-cart">
          <button
            disabled={this.props.cartIsPending}
            className="clear-button"
            onClick={e => this.handleClearAll(this.props.list.listNumber)}
            // onClick={e=>clearCart.clearCart() listservice}
          >
            {t('myFavourites.clearList')}
          </button>
        </div>
      )
    );
  };

  private renderHeader = () => {
    const { t } = this.props;
    return (
      <div className="rp-list-items-list--header">
        {this.state.showModal && this.renderModal()}
        {this.state.showAlertModal && this.renderAlertModal()}
        <div className="rp-list-items-list--header-info">
          {this.state.editingName ? (
            <div className="rp-lists-items-list--header-info--editing">
              <div className="rp-lists-items-list--header-info--editing-input">
                <input
                  placeholder={t('common.newName')}
                  value={this.state.name}
                  onChange={e => this.handleChangeListName(e.target.value)}
                />
                <span
                  className="rpds-close rp-clear-list-input"
                  onClick={e => this.handleChangeListName('')}
                />
              </div>
              <div className="rp-list-items-list--header-info--links">
                <span onClick={this.handleUpdateName}>{t('common.save')} </span>
              </div>
            </div>
          ) : (
            <div>
              <h1 className="rp-list-items-list--header-info--name">
                {this.props.list.name === 'Favorites' &&
                env.REACT_APP_BASE_ENVIRONMENT === 'CA'
                  ? 'Favourites'
                  : this.props.list.name}
              </h1>
              <div className="rp-list-items-list--header-info-bottom">
                <div className="rp-list-items-list--header-info--total">
                  {`${
                    this.props.list.listItems
                      ? this.handleSearchItemChange().length
                      : '0'
                  } ${t('common.items_plural')}`}
                </div>
                {this.props.list.listNumber &&
                  this.props.list.listNumber !== 0 &&
                  !this.props.list.favorite && (
                    <div className="rp-list-items-list--header-info--links">
                      <span onClick={this.showEditing}>
                        {t('common.rename')}{' '}
                      </span>
                      <span onClick={this.handleClickDeleteList}>
                        {t('common.deleteAction')}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
        <div className="rp-list-items-list--header-search">
          <Debounce time="500" handler="onChange">
            <input
              className="rp-list-items-list--header-search-input"
              placeholder={t('myFavourites.enterPartNumber')}
              onChange={this.handleSearchInputChange}
            />
          </Debounce>
          <button
            className="primary-button"
            onClick={this.handleSearchItemChange}
          >
            <span className="rpds-search" />
            <label>{t('common.search')} </label>
          </button>
        </div>
        {(this.props.list.listNumber &&
          this.props.list.listNumber !== 0 &&
          !this.props.list.favorite) ||
        this.props.list.name === 'Favorites'
          ? this.clearList()
          : ''}
        {this.addListToCartButton()}
      </div>
    );
  };

  public componentDidUpdate(prevProps: IItemsListProps) {
    if (
      this.props.list &&
      prevProps.list.listItems !== this.props.list.listItems
    ) {
      if (this.props.list.listNumber && this.props.list.listNumber !== 0) {
        const items: number[] = [];
        this.handleSearchItemChange().map(index => {
          if (this.props.list.listItems[index].itemDetailsState === 'pending') {
            items.push(this.props.list.listItems[index].listItemNumber);
          }
        });
        let i: number = 0;
        for (i = 0; i < items.length; i = i + 10) {
          this.props.handleItemsDetailsUpdate(items.slice(i, i + 10));
        }
      }
    }
  }

  /* Check if Consignment Warehouse exists */
  private handleConsignmentFlag = (itemId:any, flag:any) => {
    this.setState(prev => ({
       consignmentFlags: {
          ...prev.consignmentFlags,
          [itemId]: flag
       }
    }));
 };

  public render() {
    const { t } = this.props;
    return (
      <div className="rp-list-items-list">
        <div className="rp-list-items-list--alert">
          {this.state.isInvalidName && (
            <Alert
              styleClass="rp-lists-alert"
              message={'White spaces are not allowed'}
              icon={'rpds-error'}
              handleCloseAlert={() => {
                return;
              }}
              type={'error'}
            />
          )}
          {this.props.error.hasFailed && (
            <Alert
              styleClass="rp-lists-alert"
              message={this.props.error.message}
              icon={'rpds-error'}
              handleCloseAlert={() => {
                return;
              }}
              type={'error'}
            />
          )}
          {this.props.cartError.hasFailed && (
            <Alert
              styleClass="rp-lists-alert"
              message={this.props.cartError.message}
              icon={'rpds-error'}
              handleCloseAlert={() => {
                return;
              }}
              type={'error'}
            />
          )}

          {this.props.success.hasRemovedSucced && 
            <Alert
              styleClass="rp-lists-alert"
              message={`${this.props.success.message}`}
              icon={'rpds-check-circle'}
              handleCloseAlert={this.props.closeRemoveAlertMessage}
              type={'success'}
            />
          }

          {((!this.props.cartIsPending &&
              this.props.cartSuccess.hasSucced)) && (
            <Alert
              styleClass="rp-lists-alert"
              message={`${this.props.cartSuccess.message}`}
              icon={'rpds-check-circle'}
              handleCloseAlert={this.props.closeAlertMessage}
              type={'success'}
            >
              {this.props.cartSuccess.hasSucced && (
                <a
                  href={cartUrl()}
                  onClick={e => goToCart()}
                  className="reloadLink"
                >
                  {t('myFavourites.goToCart')}
                </a>
              )}
            </Alert>
          )}
        </div>
          {this.props.cartIsPending && 
          <div className='list-loader-container'>
           <Loader />
          </div> 
          }
       
        {this.props.updating && <div className="rp-list-items-list--disable" />}
        {this.renderHeader()}
        {this.props.list &&
          this.props.list.listItems &&
          (Object.keys(this.props.list.listItems).length === 0 &&
          this.props.list.listItems.constructor === Object ? (
            this.props.list.name === 'My Frequent Purchases' ? (
              <p
                style={{ width: '100%', textAlign: 'center', padding: '24px' }}
              >
                {t('myFavourites.noPurchaseTxt')}
              </p>
            ) : null
          ) : (
            this.handleSearchItemChange().map(index => (
              <>
                <VisibilitySensor
                  key={this.props.list.listItems[index].listItemNumber}
                  onChange={(visible: boolean) => {
                    this.updateItem(visible, this.props.list.listItems[index]);
                  }}
                  intervalCheck={true}
                  intervalDelay={500}
                  scrollCheck={true}
                  scrollDelay={250}
                  delayedCall={true}
                  partialVisibility={true}
                  offset={200}
                >
                  {this.props.list.listItems[index].itemDetailsState ===
                    'pending' ||
                  this.props.list.listItems[index].itemDetailsState ===
                    'loading' ? (
                    <ItemLoader />
                  ) : this.props.list.listNumber &&
                    this.props.list.listNumber !== 0 ? (
                    <>
                      <Item
                        id={this.props.list.listItems[index].listItemNumber}
                        mfc={this.props.list.listItems[index].manufacturerCode}
                        name={this.props.list.listItems[index].name}
                        category={this.props.list.listItems[index].category}
                        description={
                          this.props.list.listItems[index].description
                        }
                        image={this.props.list.listItems[index].image}
                        qty={this.props.list.listItems[index].quantity}
                        price={this.props.list.listItems[index].price}
                        retailPrice={
                          this.props.list.listItems[index].retailPrice
                        }
                        total={this.props.list.listItems[index].total}
                        inStock={this.props.list.listItems[index].inStock}
                        shipmentMarking={
                          this.props.list.listItems[index].shipmentMarking
                        }
                        warrantyProvider={
                          this.props.list.listItems[index].warrantyProvider
                        }
                        dispatch={this.props.list.listItems[index].dispatch}
                        deliverFrom={
                          this.props.userData.address &&
                          this.props.userData.address.number.toString()
                        }
                        warehouse={
                          this.props.userData.warehouseInfo &&
                          this.props.userData.warehouseInfo.name
                        }
                        warranties={this.props.warranties}
                        handleItemUpdate={this.props.handleItemUpdate}
                        handleRemoveItem={this.handleClickRemoveItem}
                        handleAddToCart={this.props.handleAddToCart}
                        replacementMfc={
                          this.props.list.listItems[index].replacementMfc
                        }
                        replacementPart={
                          this.props.list.listItems[index].replacementPart
                        }
                        alternateProductNumber={
                          this.props.list.listItems[index]
                            .alternateProductNumber
                        }
                        alternateProductManufacturer={
                          this.props.list.listItems[index]
                            .alternateProductManufacturer
                        }
                        oemProductNumber={
                          this.props.list.listItems[index].oemProductNumber
                        }
                        oemProductManufacturer={
                          this.props.list.listItems[index]
                            .oemProductManufacturer
                        }
                        onConsignmentCheck={this.handleConsignmentFlag}
                        warehouseCosignment={this.props.list.listItems[index]
                          .warehouseDefault}
                      />
                    </>
                  ) : (
                    <SpecialItem
                      id={this.props.list.listItems[index].listItemNumber}
                      mfc={this.props.list.listItems[index].manufacturerCode}
                      name={this.props.list.listItems[index].name}
                      description={this.props.list.listItems[index].description}
                      image={this.props.list.listItems[index].image}
                      price={this.props.list.listItems[index].price}
                      retailPrice={this.props.list.listItems[index].retailPrice}
                      total={this.props.list.listItems[index].total}
                      inStock={this.props.list.listItems[index].inStock}
                      deliverFrom={
                        this.props.userData.address &&
                        this.props.userData.address.number.toString()
                      }
                      warehouse={
                        this.props.userData.warehouseInfo &&
                        this.props.userData.warehouseInfo.code
                      }
                      warranties={this.props.warranties}
                      handleAddToCart={this.props.handleAddToCart}
                      replacementMfc={
                        this.props.list.listItems[index].replacementMfc
                      }
                      replacementPart={
                        this.props.list.listItems[index].replacementPart
                      }
                      alternateProductNumber={
                        this.props.list.listItems[index].alternateProductNumber
                      }
                      alternateProductManufacturer={
                        this.props.list.listItems[index]
                          .alternateProductManufacturer
                      }
                      oemProductNumber={
                        this.props.list.listItems[index].oemProductNumber
                      }
                      oemProductManufacturer={
                        this.props.list.listItems[index].oemProductManufacturer
                      }
                      warehouseName={
                        this.props.userData.warehouseInfo?.name
                      }
                      onConsignmentCheck={this.handleConsignmentFlag}
                      warehouseCosignment={this.props.list.listItems[index]
                        .warehouseDefault}
                    />
                  )}
                </VisibilitySensor>
              </>
            ))
          ))}
      </div>
    );
  }
}

export default ItemsList;
